import {
  ActionReducerMapBuilder,
  createSlice,
  PayloadAction,
  SliceCaseReducers,
} from "@reduxjs/toolkit";
import { Auth0State, getInitialAuth0LoginState } from "models/auth0LoginState";
import { AuthState, getInitialAuthState } from "models/authState";
import { Flag } from "models/FlagTypes";
import { getDefaultUserLanguage, Language } from "models/Setting";
import { getInitialUserState, User } from "models/user";
import { TechnicianSettings, UserEndpointResult, UserSettings } from "operations/schema/schema";
import { RootState } from "store";
import { asyncMutations, mutationBuilder } from "./user.mutations";
import { asyncQueries, queryBuilder } from "./user.queries";

// Interface for state
export interface State {
  authVar: AuthState;
  auth0Var: Auth0State;
  userVar: User | null;
  userEndpoints: UserEndpointResult;
  userSettings: UserSettings;
  technicianSettings: TechnicianSettings;
  languageVar: Language;
  featureFlagVar: {
    isActive: boolean;
    name: string;
  }[];
  autoSyncDocuments: boolean;
}

// Interface for store reducers
interface Reducers extends SliceCaseReducers<State> {
  setAuth: (state: State, action: PayloadAction<AuthState>) => State;
  setAuth0: (state: State, action: PayloadAction<Auth0State>) => State;
  setUser: (state: State, action: PayloadAction<User>) => State;
  setUserEndpoints: (state: State, action: PayloadAction<UserEndpointResult>) => State;
  setLanguage: (state: State, action: PayloadAction<Language>) => State;
  setTechnicianSettings: (state: State, action: PayloadAction<TechnicianSettings>) => State;
  setLoggedOut: (state: State) => State;
  setAutoSyncDocuments: (state: State, action: PayloadAction<boolean>) => State;
}
/** Example function interface
 * setOpen: (
 *    state: State,
 *    action: PayloadAction<{ dialogName: string; open: boolean }>
 * ) => State;
 */

// Interface for store selectors (if necessary)
interface Selectors {
  isFlagEnabled: (state: RootState, flagName: Flag) => boolean;
  selectAuth: (state: RootState) => AuthState;
  selectAuth0: (state: RootState) => Auth0State;
  selectLanguage: (state: RootState) => Language;
  selectUser: (state: RootState) => User | null;
  selectUserEndpoints: (state: RootState) => UserEndpointResult;
  selectUserSettings: (state: RootState) => UserSettings;
}
/** Example function interface
 * selectAuth: (
 *    state: RootState
 * ) => AuthState;
 */

// Definition of actual (initial) state
const initialState: State = {
  authVar: getInitialAuthState(),
  auth0Var: getInitialAuth0LoginState(),
  userVar: getInitialUserState(),
  languageVar: getDefaultUserLanguage(),
  featureFlagVar: [],
  userEndpoints: {
    email: "",
    useMultipleEndpoints: false,
    userEndpoints: [],
  },
  userSettings: {
    email: "",
    useMultipleEndpoints: false,
    userEndpoints: [],
  },
  technicianSettings: {
    allowChangeInvoiceType: false,
    allowNegativeStock: false,
    allowEditMachineInstallDate: false,
    isAllowedMobileMachineEdit: false,
    canCreateJob: false,
    customerSatisfactionEnabled: false,
    simplePlannerAccess: false,
    advancedPlannerAccess: false,
    mapboxToken: "",
    requireActionCode1: false,
    requireActionCode2: false,
    requireActionCode3: false,
    requireActionText: false,
    requireActionTextReturn: false,
    requireCheckListId: false,
    requireCustomerSignature: false,
    requireCustomerSignatureName: false,
    requiredSolutionDescription: false,
    requireTechnicianSignature: false,
    requireMeterReading: false,
    requireReference: false,
    requireSymptomCode1: false,
    requireSymptomCode2: false,
    requireSymptomCode3: false,
    requireSymtomText: false,
    requireTravelTime: false,
    requireTravelTimeReturn: false,
    requireWorkTime: false,
    requireWorkTimeReturn: false,
    sendMailToCustomer: false,
    syncCostPrice: false,
    syncSalesPrice: false,
    storeSignaturesLocal: false,
    workTimeStart: "07:00",
    workTimeEnd: "17:00",
    actionCodeStrongRelation: false,
    symptoCodeStrongRelation: false,
  },
  autoSyncDocuments: false,
};

// Definition of actual reducers
const reducers: Reducers = {
  setAuth: (state, { payload }) => {
    state.authVar = payload;
    return state;
  },
  setAuth0: (state, { payload }) => {
    state.auth0Var = payload;
    return state;
  },
  setUser: (state, { payload }) => {
    state.userVar = payload;
    return state;
  },
  setUserEndpoints: (state, { payload }) => {
    state.userEndpoints = payload;
    return state;
  },
  setLanguage: (state, { payload }) => {
    state.languageVar = payload;
    return state;
  },
  setTechnicianSettings: (state, { payload }) => {
    state.technicianSettings = payload;
    return state;
  },
  setLoggedOut: (state) => {
    state.userVar = null;
    state.authVar = { isLoggedIn: false };
    return state;
  },
  setAutoSyncDocuments: (state, { payload }) => {
    state.autoSyncDocuments = payload;
    return state;
  },
};
/** Example function
 * setOpen: (state, { payload: { dialogName, open } }) => {
 *    state[dialogName] = open;
 *    return state;
 * },
 */

// Definition of actual selectors
const selectors: Selectors = {
  isFlagEnabled: ({ user }, flagName) =>
    !!user.featureFlagVar.find((f) => f.name === flagName)?.isActive,
  selectAuth: ({ user }) => user.authVar,
  selectAuth0: ({ user }) => user.auth0Var,
  selectLanguage: ({ user }) => user.languageVar,
  selectUser: ({ user }) => user.userVar,
  selectUserEndpoints: ({ user }) => user.userEndpoints,
  selectUserSettings: ({ user }) => user.userSettings,
};
/** Example function
 * selectAuth: ({user}) => user.authVar
 */

// * [storename]: Name of store with lowercase letters
const storeBase = createSlice<State, Reducers>({
  name: "user",
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<State>) => {
    queryBuilder(builder);
    mutationBuilder(builder);
  },
});

// To be imported and added in store/reducers
export default storeBase.reducer;
export const {
  setAuth,
  setAuth0,
  setUser,
  setLanguage,
  setUserEndpoints,
  setLoggedOut,
  setAutoSyncDocuments,
} = storeBase.actions;
export const {
  getPublicFeatureFlags,
  getUserEndpoints,
  getUserSettings,
  getPartnerData,
  getPermissionSettings,
} = asyncQueries;

export const {
  preLogin,
  loginAuth0,
  logout,
  updateUserConsent,
  updateUserSettings,
  updateTechnicianMetadata,
} = asyncMutations;

export const {
  isFlagEnabled,
  selectAuth,
  selectAuth0,
  selectLanguage,
  selectUser,
  selectUserEndpoints,
  selectUserSettings,
} = selectors;
